import React from 'react'
import Grid from '@mui/material/Grid'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Snackbar from '@mui/material/Snackbar'
import Stack from '@mui/material/Stack'
import { Helmet } from 'react-helmet'
import { RootState } from '../../store'
import { PageViewport } from '../../components/PageLayout'
import { getTopbarHeight, useDimensions } from '../../hooks/useDimensions'
import { FooterBarComponent } from '../FooterBar'
import { GridItemComponent } from '../Home/styled'
import { getPortalBootstrapForce } from '../Profile/selectors/api/bootstrap'
import { PortalListComponent } from './List'
import { useDispatch, useSelector, useStore } from 'react-redux'
import { useFirebase } from 'react-redux-firebase'
import { getProfileData } from '../Profile/selectors'
import { PortalTransactionsComponent } from './Transactions'
import { PortalSubscriptionsComponent } from './Subscriptions'
import { actions as profileActions } from '../Profile/reducer'
import { ProfileUpdateModalComponent } from './ProfileModal'
import { ProfileFormValues, Subscription } from '../Profile/types'
import { ProfileUnitsModalComponent } from './UnitsModal'
import { getConfirmCancel, getIsUnitsModalOpen } from './selectors'
import { actions } from './reducer'
import {
  getCheckDate,
  getSubscriptionCreatedAt,
} from '../Profile/commands/subscriptions/utils/getCheckDate'
import { UnitCancel } from '../Units/reducer'
import { getUnitsIndividual, getUnitsCancel } from '../Units/selectors'
import {
  cancelSubscriptionList,
  cancelSubscriptionListError,
} from '../Profile/commands/subscriptions/cancel'
import { onError } from '../../utils/onError'
import { AppBarComponent } from '../AppBar'
import { PortalMultiFactorModal } from './MultiFactorModal'
import { clientLogger } from '../../utils/observability'

const parseUnitMetadata = (subscription?: Subscription) => {
  const raw = subscription?.order?.description || subscription?.name || ''
  const [sizeRaw, unitNumberRaw] = raw.split('/')
  const size = sizeRaw?.trim() || undefined
  const unitNumber = unitNumberRaw?.trim() || undefined

  return {
    size,
    unitNumber,
  }
}

export const PortalComponent: React.FC = () => {
  const dispatch = useDispatch()
  const store = useStore()
  const firebase = useFirebase()
  const { height, width } = useDimensions()
  const topbarHeight = getTopbarHeight(width)
  const [isUpdateModalOpen, setIsUpdateModalOpen] = React.useState(false)
  const [isMultiFactorModalOpen, setIsMultiFactorModalOpen] =
    React.useState(false)
  const [isMultiFactorEnabled, setIsMultiFactorEnabled] = React.useState(false)
  const isUnitsModalOpen = useSelector(getIsUnitsModalOpen)
  const data = useSelector(getProfileData)

  const unitsCancel = useSelector(getUnitsCancel)
  const individualUnits = useSelector(getUnitsIndividual)
  const confirmCancel = useSelector(getConfirmCancel)
  const _cancelSubscriptionError = useSelector(cancelSubscriptionListError)
  const profile = data?.profile || {}
  const transactions = data?.transactions || []
  const subscriptions = data?.subscriptions || []

  const checkDate = getCheckDate(getSubscriptionCreatedAt(confirmCancel))

  React.useEffect(() => {
    if (_cancelSubscriptionError) onError('Cancel Subscription Error')
  }, [_cancelSubscriptionError])

  React.useEffect(() => {
    const syncMultiFactorState = (user: any) => {
      setIsMultiFactorEnabled(
        Boolean(user?.multiFactor?.enrolledFactors.length)
      )
    }

    syncMultiFactorState(firebase.auth().currentUser)

    return firebase.auth().onIdTokenChanged(syncMultiFactorState)
  }, [firebase])

  const onOpenProfileModal = React.useCallback(async () => {
    clientLogger.action('portal_profile_modal_opened', {
      customerProfileId: profile.customerProfileId,
    })
    await getPortalBootstrapForce(store.getState() as RootState)
    const latestProfile = getProfileData(store.getState() as RootState).profile
    dispatch(profileActions.onIsAchDebit(Boolean(latestProfile.routingNumber)))
    dispatch(
      profileActions.onProfileForm({
        ...latestProfile,
        ...{ cardNumber: '', expirationDate: '', cardCode: '' },
        ...{
          routingNumber: '',
          accountNumber: '',
          accountNumberConfirm: '',
        },
      })
    )
    setIsUpdateModalOpen(true)
  }, [setIsUpdateModalOpen, dispatch, store])

  const onOpenUnitsModal = React.useCallback(() => {
    clientLogger.action('portal_units_modal_opened', {
      customerProfileId: profile.customerProfileId,
    })
    dispatch(actions.onOpenUnitsModal(true))
  }, [dispatch, profile.customerProfileId])

  const onOpenMultiFactorModal = React.useCallback(() => {
    clientLogger.action('portal_mfa_modal_opened', {
      customerProfileId: profile.customerProfileId,
      isMultiFactorEnabled,
    })
    setIsMultiFactorModalOpen(true)
  }, [isMultiFactorEnabled, profile.customerProfileId])

  const onCloseMultiFactorModal = React.useCallback(() => {
    setIsMultiFactorModalOpen(false)
  }, [])

  const onCloseUnitsModal = React.useCallback(() => {
    dispatch(actions.onOpenUnitsModal(false))
  }, [dispatch])

  const onIsConfirmOpen = React.useCallback(
    (val: Subscription) => {
      dispatch(actions.onConfirmCancel(val))
    },
    [dispatch]
  )

  const onIsConfirmClose = React.useCallback(
    (val: undefined) => {
      dispatch(actions.onConfirmCancel(val))
    },
    [dispatch]
  )

  const onCancelSubscription = React.useCallback(() => {
    if (!confirmCancel) return
    const matchedUnit = individualUnits.find(
      (unit) =>
        String(unit.subscriptionId) === String(confirmCancel.subscriptionId) &&
        Number(unit.customerProfileId) === Number(profile.customerProfileId)
    )
    const parsedUnitMetadata = parseUnitMetadata(confirmCancel)
    const model: UnitCancel = {
      subscriptionId: confirmCancel.subscriptionId,
      customerProfileId: profile.customerProfileId,
      phoneNumber: profile.phoneNumber,
      fullName: profile.fullName,
      email: profile.email,
      unitId: matchedUnit?.id,
      masterId: matchedUnit?.masterId,
      unitNumber: matchedUnit?.unitNumber ?? parsedUnitMetadata.unitNumber,
      size: matchedUnit?.size ?? parsedUnitMetadata.size,
    }
    model.lastPayment = checkDate.startOfMonth
    model.moveOut = checkDate.endOfMonth
    clientLogger.action('portal_subscription_cancel_confirmed', {
      subscriptionId: confirmCancel.subscriptionId,
      customerProfileId: profile.customerProfileId,
      unitId: matchedUnit?.id,
      masterId: matchedUnit?.masterId,
      moveOut: model.moveOut,
    })
    cancelSubscriptionList(model)
    dispatch(actions.onConfirmCancel(undefined))
  }, [
    checkDate,
    confirmCancel,
    dispatch,
    individualUnits,
    profile.customerProfileId,
    profile.email,
    profile.fullName,
    profile.phoneNumber,
  ])

  const onClose = React.useCallback(() => {
    dispatch(profileActions.onProfileForm(ProfileFormValues))
    setIsUpdateModalOpen(false)
  }, [setIsUpdateModalOpen, dispatch])

  return (
    <>
      <Helmet>
        <title>Portal || JustLockIt</title>
        <meta name="keywords" content="HTML,CSS,JavaScript" />
        <meta
          name="description"
          content="justlockit.com portal page. storage units Schulenburg, Texas"
        />
      </Helmet>
      <AppBarComponent fullWidth />
      <PageViewport className="portal-page" viewportHeight={height}>
        <Grid
          className="portal-page__grid"
          container
          rowSpacing={2}
          columnSpacing={{ xs: 1, sm: 2, md: 3 }}
          sx={{ mt: `${topbarHeight}px` }}
        >
          <Grid
            className="portal-page__sidebar"
            item
            xs={12}
            sm={12}
            md={4}
            lg={3}
          >
            <GridItemComponent className="portal-page__panel portal-page__panel--summary">
              <PortalListComponent
                isMultiFactorEnabled={isMultiFactorEnabled}
                profile={profile}
                onOpenMultiFactorModal={onOpenMultiFactorModal}
                onOpenProfileModal={onOpenProfileModal}
                onOpenUnitsModal={onOpenUnitsModal}
              />
            </GridItemComponent>
          </Grid>
          <Grid
            className="portal-page__subscriptions"
            item
            xs={12}
            sm={12}
            md={8}
            lg={9}
          >
            <GridItemComponent
              className="portal-page__panel portal-page__panel--subscriptions"
              sx={{ height: 'auto' }}
            >
              <PortalSubscriptionsComponent
                subscriptions={subscriptions}
                onIsConfirmOpen={onIsConfirmOpen}
                unitsCancel={unitsCancel}
              />
            </GridItemComponent>
          </Grid>
          <Grid className="portal-page__transactions" item xs={12}>
            <GridItemComponent
              className="portal-page__panel portal-page__panel--transactions"
              sx={{ height: 'auto' }}
            >
              <PortalTransactionsComponent transactions={transactions} />
            </GridItemComponent>
          </Grid>
        </Grid>
      </PageViewport>
      <FooterBarComponent fullWidth />
      {isUpdateModalOpen && <ProfileUpdateModalComponent onClose={onClose} />}
      {isUnitsModalOpen && (
        <ProfileUnitsModalComponent onClose={onCloseUnitsModal} />
      )}
      {isMultiFactorModalOpen && (
        <PortalMultiFactorModal onClose={onCloseMultiFactorModal} />
      )}
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={Boolean(confirmCancel)}
        onClose={() => onIsConfirmClose(undefined)}
        message={`Cancel Subscription for ${confirmCancel?.name} ?`}
      >
        <Alert
          onClose={() => onIsConfirmClose(undefined)}
          severity="info"
          action={
            <Button
              onClick={onCancelSubscription}
              color="error"
              size="small"
              variant="contained"
              sx={{
                alignSelf: 'center',
              }}
            >
              Confirm
            </Button>
          }
          sx={{ width: '100%' }}
        >
          <Stack
            direction="column"
            justifyContent="center"
            alignItems="start"
            spacing={0.5}
          >
            <div>{`Cancel Subscription for ${confirmCancel?.name} ?`}</div>
            <div>{checkDate.msg0}</div>
            <div>{checkDate.msg1}</div>
          </Stack>
        </Alert>
      </Snackbar>
    </>
  )
}
